import {
    Box,
    Grid,
    TextField,
    Typography,
    Checkbox,
    FormControlLabel,
    IconButton,
    Button,
    Chip
} from '@mui/material';
import { useViewExecutionVisit } from './hooks/useViewExecutionVisit';
import { useTranslation } from 'react-i18next';
import BackdropComponent from '../../../components/Backdrop/BackdropComponent';
import { useViewExecutionVisitDownloads } from './hooks/useViewExecutionVisitDownloads';
import DownloadIcon from '@mui/icons-material/Download';
import { useViewExecutionVisitPresentacion } from './hooks/useViewExecutionVisitPresentacion';
import { VisitaEstadoChip } from '../components/EstadoChip';
import { PresentationStateChip } from '../../Presentaciones/components/PresentationStateChip';
import { useNavigate } from 'react-router-dom';

function ViewExecutionVisit() {
    const { t } = useTranslation('visitas');
    const { formData, isLoading } = useViewExecutionVisit();
    const { handleDownloadVerosimilitud, handleDownloadRgrl, handleDownloadActa } =
        useViewExecutionVisitDownloads(formData);
    const { presentacion, loadingPresentacion } = useViewExecutionVisitPresentacion(
        formData?.idPresentacion || null
    );
    const navigate = useNavigate();
    if (isLoading || !formData) return <BackdropComponent loading={true} />;

    const showAditionalData = formData?.nombreTipoVisita !== 'GENERICA';

    const showVerosimilitudDownload = formData?.nombreTipoVisita === 'VEROSIMILITUD';

    const showRgrlDownload =
        formData?.nombreTipoVisita === 'VEROSIMILITUD' || formData?.nombreTipoVisita === 'RGRL';

    return (
        <>
            <Box sx={{ mt: 4, backgroundColor: 'white', p: 4 }}>
                <Typography variant="h3" mb={3}>
                    <strong> {t('newVisit.sectionSummary')}</strong>
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={<Checkbox checked={formData.esFallida} disabled />}
                            label={t('newVisit.label.failedVisit')}
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TextField
                            label={t('newVisit.label.totalStaff')}
                            fullWidth
                            value={formData.totalPersonal ?? ''}
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TextField
                            label={t('newVisit.label.administrativeStaff')}
                            fullWidth
                            value={formData.administrativos ?? ''}
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TextField
                            label={t('newVisit.label.productionStaff')}
                            fullWidth
                            value={formData.personalProduccion ?? ''}
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TextField
                            label={t('newVisit.label.safetyService')}
                            fullWidth
                            value={
                                formData.seguridadHigiene
                                    ? t('newVisit.common.yes')
                                    : t('newVisit.common.no')
                            }
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TextField
                            label={t('newVisit.label.professional')}
                            fullWidth
                            value={formData.profesional}
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TextField
                            label={t('newVisit.label.license')}
                            fullWidth
                            value={formData.matricula}
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label={t('newVisit.label.observation')}
                            fullWidth
                            multiline
                            rows={3}
                            value={formData.observacion}
                            disabled
                        />
                    </Grid>

                    {formData.firmaPreventor && (
                        <Grid item xs={12} sm={6}>
                            <Box
                                component="img"
                                src={formData.firmaPreventor}
                                alt="Firma Preventor"
                                sx={{
                                    width: '100%',
                                    height: 150,
                                    background: '#F6F6F6',
                                    border: '1px solid #ccc'
                                }}
                            />
                            <Typography>{t('newVisit.label.preventorSignature*')}</Typography>
                        </Grid>
                    )}

                    {formData.firmaResponsable && (
                        <Grid item xs={12} sm={6}>
                            <Box
                                component="img"
                                src={formData.firmaResponsable}
                                alt="Firma Responsable"
                                sx={{
                                    width: '100%',
                                    height: 150,
                                    background: '#F6F6F6',
                                    border: '1px solid #ccc'
                                }}
                            />
                            <Typography>{t('newVisit.label.responsibleSignature')}</Typography>
                        </Grid>
                    )}
                </Grid>
            </Box>
            {showAditionalData && (
                <Box
                    component="div"
                    justifyContent="center"
                    sx={{ mt: 4, backgroundColor: 'white', p: 2 }}
                >
                    <Box display="flex" alignItems="center" gap={2} mb={3}>
                        <Typography variant="h3" mb={3}>
                            <strong>{t('newVisit.aditionalData')}</strong>
                        </Typography>
                    </Box>
                    <Box display="flex" flexDirection="column" gap={2}>
                        {showVerosimilitudDownload && (
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ borderBottom: '1px solid #ccc', padding: '6px 4px' }}
                            >
                                <Box display="flex" alignItems="center" gap={2}>
                                    <Typography variant="body1">
                                        {t('labels.verosimilitud')}
                                    </Typography>
                                    {!formData.esFallida ? (
                                        <Chip
                                            label={t('newVisit.state.complete')}
                                            sx={{
                                                backgroundColor: '#E3F5E3',
                                                color: '#2D6830',
                                                fontWeight: 600
                                            }}
                                        />
                                    ) : (
                                        <VisitaEstadoChip estadoId={2} />
                                    )}
                                </Box>
                                <IconButton onClick={handleDownloadVerosimilitud}>
                                    <DownloadIcon />
                                </IconButton>
                            </Box>
                        )}

                        {showRgrlDownload && (
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ borderBottom: '1px solid #ccc', padding: '6px 4px' }}
                            >
                                <Box display="flex" alignItems="center" gap={2}>
                                    <Typography variant="body1">{t('labels.rgrl')}</Typography>
                                    {!loadingPresentacion &&
                                        (!formData.esFallida ? (
                                            <Chip
                                                label={t('newVisit.state.complete')}
                                                sx={{
                                                    backgroundColor: '#E3F5E3',
                                                    color: '#2D6830',
                                                    fontWeight: 600
                                                }}
                                            />
                                        ) : (
                                            presentacion?.idEstado !== undefined && (
                                                <PresentationStateChip
                                                    presentationState={presentacion.idEstado}
                                                />
                                            )
                                        ))}
                                </Box>
                                <IconButton onClick={handleDownloadRgrl}>
                                    <DownloadIcon />
                                </IconButton>
                            </Box>
                        )}
                    </Box>
                </Box>
            )}
            <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                gap={2}
                sx={{ mt: 4, backgroundColor: 'white', p: 2 }}
            >
                <Button variant="outlined" onClick={() => navigate(-1)}>
                    {t('buttons.goBack')}
                </Button>
                <Button variant="contained" color="primary" onClick={handleDownloadActa}>
                    {t('table.actions.downloadActa')}
                </Button>
            </Box>
        </>
    );
}

export default ViewExecutionVisit;
